"use client";

import NumberFlow from "@number-flow/react";
import { Button, SubmitButton } from "@/components/ui/button";
import {
  Modal,
  ModalBody,
  ModalClose,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Icon } from "@/components/ui/icon";
import { initials } from "@/lib/string/initials";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";
import { useCreditBalance } from "@/features/billing";

/** Reveal charges this many credits up front; refunded if no contacts found. */
const REVEAL_CREDITS = 2;

/** Docs page explaining how Arbi sources and verifies contact data. */
const CONTACT_DATA_DOCS_URL = "https://docs.arbi.ai/contact-data";

interface CandidatePreview {
  name: string;
  jobTitle?: string | null;
  company?: string | null;
  avatarUrl?: string | null;
}

type ContactRevealConfirmActionProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void> | void;
  loading?: boolean;
  candidate?: CandidatePreview | null;
  /** "reveal" for search-sourced profiles, "enrich" for imported / ATS. */
  action?: "reveal" | "enrich";
};

export function ContactRevealConfirmAction({
  open,
  onOpenChange,
  onConfirm,
  loading,
  candidate,
  action = "reveal",
}: ContactRevealConfirmActionProps) {
  const { data: credits } = useCreditBalance();
  const balance = credits?.balance ?? null;
  const projectedBalance =
    balance != null ? Math.max(balance - REVEAL_CREDITS, 0) : null;
  const verb = action === "enrich" ? "enrich" : "reveal";

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="max-w-lg">
        <ModalHeader
          title={
            action === "enrich" ? "Enrich Information" : "Reveal Information"
          }
        />
        <ModalBody className="flex flex-col gap-4">
          {candidate && (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-inset p-3">
              <Avatar className="size-10 shadow-sm ring-1 ring-inset ring-[color:var(--border-subtle-alpha)]">
                <AvatarImage src={candidate.avatarUrl} alt={candidate.name} />
                <AvatarFallback className="bg-surface-inset text-xs font-semibold leading-none text-primary">
                  {initials(candidate.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-sm font-medium text-heading">
                  {candidate.name}
                </span>
                {(candidate.jobTitle || candidate.company) && (
                  <span className="truncate text-xs text-muted-foreground">
                    {[
                      candidate.jobTitle &&
                        titleCasePreserveAcronyms(candidate.jobTitle),
                      candidate.company &&
                        titleCasePreserveAcronyms(candidate.company),
                    ]
                      .filter(Boolean)
                      .join(" at ")}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Credit cost panel — mirrors the gold credit treatment in the top bar */}
          <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface-inset">
            <div className="flex items-start gap-3 p-3">
              <div className="relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-contrast/6 bg-contrast/4">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[rgba(255,175,83,0.18)]" />
                <Icon name="IconCoin1" size={18} className="relative text-ai" />
              </div>
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium text-heading">
                  {REVEAL_CREDITS} credits to {verb}
                </span>
                <span className="text-xs leading-snug text-muted-foreground">
                  {REVEAL_CREDITS} credits are charged immediately. If no
                  contact information is found, the credits are automatically
                  refunded to your balance.
                </span>
              </div>
            </div>

            {/* What the reveal searches for */}
            <div className="flex flex-col gap-2 border-t border-border px-3 py-2.5">
              <div className="flex items-center gap-2 text-xs text-foreground">
                <Icon
                  name="IconEmail1"
                  size={14}
                  className="shrink-0 text-foreground opacity-50"
                />
                Personal &amp; work email addresses
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground">
                <Icon
                  name="IconPhoneDynamicIsland"
                  size={14}
                  className="shrink-0 text-foreground opacity-50"
                />
                Direct phone numbers
              </div>
            </div>

            {/* Enrichment + verification — sourced and scored across our data vendor network */}
            <div className="flex flex-col gap-3 border-t border-border px-3 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Icon
                    name="IconShieldCheck"
                    size={15}
                    className="shrink-0 text-ai"
                  />
                  <span className="text-sm font-medium text-heading">
                    Sourced across many trusted providers
                  </span>
                </div>
                <a
                  href={CONTACT_DATA_DOCS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-px inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary transition-opacity hover:opacity-80"
                >
                  Learn more
                  <Icon
                    name="IconSquareArrowTopRight2"
                    size={11}
                    className="text-primary"
                  />
                </a>
              </div>
              <p className="text-xs leading-snug text-muted-foreground">
                Every email and phone number is enriched across our network of
                data providers and scored for deliverability, so you always know
                what you can trust.
              </p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                <div className="flex items-center gap-2 text-xs text-foreground">
                  <Icon
                    name="IconSubscriptionTick1"
                    size={14}
                    className="shrink-0 text-ai"
                  />
                  Verified
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground">
                  <Icon
                    name="IconExclamationCircle"
                    size={14}
                    className="shrink-0 text-yellow-500"
                  />
                  Risky
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground">
                  <Icon
                    name="IconCircleX"
                    size={14}
                    className="shrink-0 text-destructive"
                  />
                  Undeliverable
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground">
                  <Icon
                    name="IconQuestionmarkCircle2"
                    size={14}
                    className="shrink-0 text-muted-foreground"
                  />
                  Unknown
                </div>
              </div>
            </div>

            {balance != null && (
              <div className="flex items-center justify-between border-t border-border bg-contrast/2 px-3 py-2.5">
                <span className="text-xs font-medium text-muted-foreground">
                  Changes to your balance
                </span>
                <span className="flex items-center gap-2 text-sm">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Icon name="IconCoin1" size={13} className="text-ai" />
                    <NumberFlow value={balance} className="font-medium" />
                  </span>
                  <Icon
                    name="IconArrowRight"
                    size={13}
                    className="text-foreground opacity-40"
                  />
                  <span className="flex items-center gap-1 text-heading">
                    <Icon name="IconCoin1" size={13} className="text-ai" />
                    <NumberFlow
                      value={projectedBalance ?? 0}
                      className="font-semibold"
                    />
                  </span>
                </span>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalClose asChild>
            <Button variant="secondary" size="sm" className="h-9 sm:h-7">
              Cancel
            </Button>
          </ModalClose>
          <SubmitButton
            variant="primary"
            size="sm"
            onClick={onConfirm}
            loading={loading}
            loadingLabel="Unlocking"
            icon={<Icon name="IconLock" size={14} />}
            className="h-9 sm:h-7"
          >
            Unlock
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
